import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import {
  EncryptionService,
  STORAGE_KEYS,
  KeyMismatchError,
} from "../services/encrypt-decrypt";

// Extend the request config type to include retry flags
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
}

// --- SERVICES & UTILITIES ---
const encryptionService = new EncryptionService();

// --- GLOBAL REINIT LOCK ---
/**
 * A promise that acts as a lock to prevent concurrent reinitialization.
 * `null` when no reinit is in progress.
 */
let reinitInProgress: Promise<void> | null = null;

// Circuit breaker to prevent infinite reinit loops
let reinitAttempts = 0;
let lastReinitTime = 0;
const MAX_REINIT_ATTEMPTS = 3;
const REINIT_COOLDOWN_MS = 10000; // 10 seconds

// --- CROSS-TAB COORDINATION ---
const REINIT_STATE_KEY = "encryption_reinit_state";
const REINIT_TTL_MS = 60_000; // consider a reinit stale after 60s
const CHANNEL_NAME = "encryption-reinit";

type ReinitState = {
  status: "in-progress" | "done" | "failed";
  startedAt: number;
};

const readReinitState = (): ReinitState | null => {
  try {
    const raw = localStorage.getItem(REINIT_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ReinitState;
    if (
      !parsed ||
      typeof parsed.startedAt !== "number" ||
      (parsed.status !== "in-progress" &&
        parsed.status !== "done" &&
        parsed.status !== "failed")
    ) {
      return null;
    }
    // Discard stale state
    if (Date.now() - parsed.startedAt > REINIT_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeReinitState = (state: ReinitState | null) => {
  if (!state) {
    localStorage.removeItem(REINIT_STATE_KEY);
    return;
  }
  localStorage.setItem(REINIT_STATE_KEY, JSON.stringify(state));
};

const broadcast = (() => {
  try {
    // eslint-disable-next-line no-undef
    const bc =
      typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel(CHANNEL_NAME)
        : null;
    return bc;
  } catch {
    return null;
  }
})();

const notifyReinitEvent = (event: "start" | "done" | "failed") => {
  if (broadcast) {
    broadcast.postMessage({ type: event });
  }
};

const waitForCrossTabReinit = async (): Promise<void> => {
  // Quick check if already not in-progress
  const current = readReinitState();
  if (!current || current.status !== "in-progress") return;

  // Wait using BroadcastChannel if available
  if (broadcast) {
    await new Promise<void>((resolve) => {
      const handler = (ev: MessageEvent) => {
        if (ev?.data?.type === "done" || ev?.data?.type === "failed") {
          broadcast.removeEventListener("message", handler as any);
          resolve();
        }
      };
      broadcast.addEventListener("message", handler as any);
    });
    return;
  }

  // Fallback: listen to storage changes
  await new Promise<void>((resolve) => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === REINIT_STATE_KEY) {
        const state = readReinitState();
        if (!state || state.status === "done" || state.status === "failed") {
          window.removeEventListener("storage", onStorage);
          resolve();
        }
      }
    };
    window.addEventListener("storage", onStorage);
  });
};

const reinitializeEncryptionWithLock = async (): Promise<void> => {
  const now = Date.now();

  // Circuit breaker check
  if (
    reinitAttempts >= MAX_REINIT_ATTEMPTS &&
    now - lastReinitTime < REINIT_COOLDOWN_MS
  ) {
    console.error("[ENCRYPTION] Too many reinit attempts, breaker active");
    throw new Error("Too many reinitialization attempts");
  }

  // If a reinit is already in progress, wait for it
  if (reinitInProgress) {
    console.log("[ENCRYPTION] Reinit already in progress, waiting...");
    return reinitInProgress;
  }

  // Create the lock *before* doing anything that changes state
  reinitAttempts++;
  lastReinitTime = now;
  let resolveWaiters: (() => void)[] = [];
  reinitInProgress = new Promise<void>(async (resolve, reject) => {
    try {
      console.warn("[ENCRYPTION] Starting reinit...");

      // Mark cross-tab state before reset/init
      writeReinitState({ status: "in-progress", startedAt: Date.now() });
      notifyReinitEvent("start");

      // Reset AFTER lock
      encryptionService.reset();

      await encryptionService.initializeEncryption();
      console.log("[ENCRYPTION] Reinit successful.");

      // Reset breaker on success
      reinitAttempts = 0;

      // Wake up pending
      resolveWaiters.forEach((fn) => fn());
      resolveWaiters = [];

      // Mark cross-tab done
      writeReinitState({ status: "done", startedAt: Date.now() });
      notifyReinitEvent("done");

      resolve();
    } catch (err) {
      console.error("[ENCRYPTION] Reinit failed:", err);
      // Wake up pending anyway
      resolveWaiters.forEach((fn) => fn());
      resolveWaiters = [];
      writeReinitState({ status: "failed", startedAt: Date.now() });
      notifyReinitEvent("failed");
      reject(err);
    } finally {
      reinitInProgress = null;
    }
  });

  return reinitInProgress;
};

/** Ensures a symmetric key is loaded; uses the global reinit lock to avoid races. */
export const ensureEncryptionReady = async (): Promise<void> => {
  if (encryptionService.isReady()) {
    return;
  }

  const keyLoaded = await encryptionService.loadSymmetricKey();
  if (keyLoaded && encryptionService.isReady()) {
    return;
  }

  await reinitializeEncryptionWithLock();
};

// --- AXIOS INSTANCE ---
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 30000,
});

const retryEncryptedRequest = async (
  config: ExtendedAxiosRequestConfig
): Promise<AxiosResponse> => {
  if (config._retry) {
    throw new Error("Encrypted request retry limit reached");
  }
  config._retry = true;
  return apiClient(config);
};

const handleDecryptionFailure = async (
  err: unknown,
  config: ExtendedAxiosRequestConfig
): Promise<AxiosResponse> => {
  if (config._retry) {
    throw err;
  }

  if (err instanceof KeyMismatchError) {
    const currentKeyId = localStorage.getItem(STORAGE_KEYS.KEY_ID);
    const isStaleResponse =
      Boolean(currentKeyId) &&
      err.responseKeyId !== currentKeyId &&
      encryptionService.isReady();

    if (isStaleResponse) {
      console.log(
        "[API] Stale encrypted response (old key), retrying request..."
      );
      return retryEncryptedRequest(config);
    }
  }

  if (reinitInProgress) {
    console.log("[API] Reinit in progress, waiting...");
    await reinitInProgress;
  } else {
    await reinitializeEncryptionWithLock();
  }

  return retryEncryptedRequest(config);
};

// --- REQUEST INTERCEPTOR ---
apiClient.interceptors.request.use(
  async (config: ExtendedAxiosRequestConfig) => {
    // Wait for any ongoing reinitialization to complete before proceeding.
    if (reinitInProgress) {
      console.log("[API] Waiting for ongoing reinit to complete...");
      await reinitInProgress;
    }

    // Cross-tab: if another tab is reinitializing, wait
    const state = readReinitState();
    if (state?.status === "in-progress") {
      console.log("[API] Waiting for cross-tab reinit to complete...");
      await waitForCrossTabReinit();
    }

    // Ensure encryption is ready. If not, trigger a reinitialization.
    if (!encryptionService.isReady()) {
      await ensureEncryptionReady();
    }

    // Abort check after all async operations.
    if (config.signal?.aborted) {
      return Promise.reject(new axios.Cancel("Request aborted"));
    }

    // Set headers with the latest token and key ID.
    const token = encryptionService.getJwtToken();
    const keyId = localStorage.getItem(STORAGE_KEYS.KEY_ID);
    const epicIntrospectUrl = localStorage.getItem(
      STORAGE_KEYS.EPIC_INTROSPECT_URL
    );

    if (config.headers) {
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (keyId) config.headers["x-key-id"] = keyId;
      if (epicIntrospectUrl) config.headers.IntrospectUrl = epicIntrospectUrl;

      // Ensure Content-Type is not set for FormData.
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      }
    }

    // If no keyId is available, don't send the request to encrypted endpoints
    if (!keyId && !encryptionService.isReady()) {
      console.warn(
        "[API] No encryption key available, skipping encrypted request"
      );
      return Promise.reject(new Error("No encryption key available"));
    }

    return config;
  },
  (error: AxiosError) => {
    console.error("[API] Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// --- RESPONSE INTERCEPTOR ---
apiClient.interceptors.response.use(
  async (response: AxiosResponse) => {
    if (!response?.data) return response;

    if (response.data.encrypted) {
      if (response.config.signal?.aborted) {
        return Promise.reject(new axios.Cancel("Request aborted"));
      }

      try {
        console.log("[ENCRYPTION] Decrypting response...");
        const decryptedData = await encryptionService.decryptResponse(
          response.data
        );
        response.data = decryptedData;
        console.log("[ENCRYPTION] Response decrypted successfully.");
      } catch (err: any) {
        console.error("[ENCRYPTION] Decryption failed:", err.message);

        const config = response.config as ExtendedAxiosRequestConfig;

        if (
          err instanceof KeyMismatchError ||
          err.message?.includes("Symmetric key not available") ||
          err.message?.includes("Key import timeout")
        ) {
          return handleDecryptionFailure(err, config);
        }

        throw err;
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    if (originalRequest?.signal?.aborted) {
      return Promise.reject(new axios.Cancel("Request aborted"));
    }

    if (
      error.response &&
      [401, 403].includes(error.response.status) &&
      !(originalRequest.data instanceof FormData) &&
      !originalRequest._retry
    ) {
      console.log(`[API] ${error.response.status} → reinit + retry`);

      // 🔑 NEW: same protection as above
      if (reinitInProgress) {
        console.log("[API] Reinit in progress, waiting...");
        await reinitInProgress;
      } else {
        await reinitializeEncryptionWithLock();
      }

      return retryEncryptedRequest(originalRequest);
    }

    if (originalRequest?.data instanceof FormData) {
      console.warn("[API] Cannot retry FormData request automatically.");
    }

    return Promise.reject(error);
  }
);

// --- DEBUG HELPER ---
export const getEncryptionStatus = () => ({
  isReady: encryptionService.isReady(),
  reinitInProgress: !!reinitInProgress,
  currentKeyId: localStorage.getItem(STORAGE_KEYS.KEY_ID),
});

// Reset circuit breaker (useful for testing or manual recovery)
export const resetReinitCircuitBreaker = () => {
  reinitAttempts = 0;
  lastReinitTime = 0;
  console.log("[ENCRYPTION] Circuit breaker reset");
};

export { apiClient, encryptionService, reinitializeEncryptionWithLock };
